<template>
  <div class="app-sidebar">
    <el-menu
      :default-active="activeMenu"
      class="sidebar-menu"
      @select="handleMenuSelect"
      :collapse="isCollapse"
      :collapse-transition="false"
    >
      <el-menu-item 
        v-for="item in menuItems" 
        :key="item.index" 
        :index="item.index"
        @click="handleMenuClick(item)"
      >
        <el-icon>
          <component :is="item.icon" />
        </el-icon>
        <template #title>
          <!-- 告警中心badge -->
          <div class="menu-title-wrapper">
            <span>{{ item.title }}</span>
            <el-badge 
              v-if="item.index === 'alarms' && pendingAlarmsCount > 0" 
              :value="pendingAlarmsCount" 
              :max="99"
              class="alarm-badge-inline">
              <span></span>
            </el-badge>
          </div>
        </template>
      </el-menu-item>
    </el-menu>
    
    <!-- 折叠/展开按钮 -->
    <div class="collapse-btn" @click="toggleCollapse">
      <el-icon>
        <Fold v-if="!isCollapse" />
        <Expand v-else />
      </el-icon>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import api from '@/utils/api'
import {
  Monitor,
  Setting,
  Warning,
  User,
  DataAnalysis,
  Location,
  Fold,
  Expand,
  Connection,
  Bell
} from '@element-plus/icons-vue'

export default {
  name: 'AppSidebar',
  components: {
    Monitor,
    Setting,
    Warning,
    User,
    DataAnalysis,
    Location,
    Fold,
    Expand,
    Connection,
    Bell
  },
  setup() {
    const router = useRouter()
    const route = useRoute()
    const isCollapse = ref(false)
    const pendingAlarmsCount = ref(0)
    let pollingTimer = null
    
    const menuItems = ref([
      {
        index: 'dashboard',
        title: '控制台',
        icon: 'Monitor',
        route: '/dashboard'
      },
      {
        index: 'devices',
        title: '设备管理',
        icon: 'Setting',
        route: '/devices'
      },
      {
        index: 'alarms',
        title: '告警中心',
        icon: 'Warning',
        route: '/alarms'
      },
      {
        index: 'alarm-simulator',
        title: '告警模拟器',
        icon: 'Setting',
        route: '/alarm-simulator'
      },
      {
        index: 'notification-test',
        title: '通知测试',
        icon: 'Message',
        route: '/notification-test'
      },
      {
        index: 'family',
        title: '家庭管理',
        icon: 'User',
        route: '/family'
      },
      {
        index: 'monitoring-reports',
        title: '监测报告',
        icon: 'DataAnalysis',
        route: '/monitoring-reports'
      },
      {
        index: 'notification-policy',
        title: '通知策略',
        icon: 'Bell',
        route: '/notification-policy'
      },
      {
        index: 'third-party-guide',
        title: '第三方对接',
        icon: 'Connection',
        route: '/third-party-guide'
      },
    ])
    
    const activeMenu = computed(() => {
      const path = route.path
      // 直接匹配完整路径
      let menuItem = menuItems.value.find(item => item.route === path)
      // 如果没有直接匹配，则检查是否为子路径
      if (!menuItem) {
        menuItem = menuItems.value.find(item => path.startsWith(item.route + '/'))
      }
      return menuItem ? menuItem.index : 'dashboard'
    })
    
    const handleMenuSelect = (index) => {
      const menuItem = menuItems.value.find(item => item.index === index)
      if (menuItem && route.path !== menuItem.route) {
        router.push(menuItem.route)
      }
    }
    
    const handleMenuClick = (item) => {
      if (route.path !== item.route) {
        router.push(item.route)
      }
    }
    
    const toggleCollapse = () => {
      isCollapse.value = !isCollapse.value
      localStorage.setItem('sidebarCollapse', JSON.stringify(isCollapse.value))
    }
    
    // 监听折叠状态变化
    const stopWatcher = watch(isCollapse, (newValue) => {
      localStorage.setItem('sidebarCollapse', JSON.stringify(newValue))
    })
    
    // 获取未处理报警数量
    const fetchPendingAlarmsCount = async () => {
      try {
        // 检查必要的认证信息
        const user = localStorage.getItem('user')
        const family = localStorage.getItem('family')
        
        if (!user || !family) {
          console.warn('缺少用户或家庭信息，跳过获取未处理报警数量')
          pendingAlarmsCount.value = 0
          return
        }
        
        // 直接使用axios的超时设置，不使用AbortController避免冲突
        const response = await api.get('/alarms/pending-count', {
          timeout: 8000 // 8秒超时，给后端足够的处理时间
        })
        
        if (response && response.success) {
          pendingAlarmsCount.value = response.data || 0
        } else {
          console.warn('获取未处理报警数量失败:', response ? response.message : '未知错误')
          // 不重置为0，保持当前值
        }
      } catch (error) {
        if (error.code === 'ECONNABORTED') {
          console.warn('获取未处理报警数量超时，将在下次轮询时重试')
        } else if (error.message === 'canceled' || error.code === 'ERR_CANCELED') {
          console.log('获取未处理报警数量请求被取消（正常现象，可能是页面切换）')
        } else {
          console.error('获取未处理报警数量失败:', error.message || error)
        }
        // 发生任何错误都不重置badge数量，保持用户体验
      }
    }
    
    // 开始轮询
    const startPolling = () => {
      // 先清理之前的定时器
      stopPolling()
      
      // 立即执行一次
      fetchPendingAlarmsCount()
      
      // 每45秒轮询一次（稍微增加间隔，减少服务器压力）
      pollingTimer = setInterval(() => {
        // 检查页面是否还在当前标签页
        if (!document.hidden) {
          fetchPendingAlarmsCount()
        }
      }, 45000)
    }
    
    // 停止轮询
    const stopPolling = () => {
      if (pollingTimer) {
        clearInterval(pollingTimer)
        pollingTimer = null
      }
    }
    
    // 页面可见性变化处理
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // 页面变为可见时，立即更新一次
        fetchPendingAlarmsCount()
      }
    }
    
    // 全局方法：更新未处理报警数量（供外部调用）
    const updatePendingAlarmsCount = (count) => {
      console.log('更新侧边栏告警计数:', count)
      pendingAlarmsCount.value = count
    }
    
    // 暴露给全局使用
    window.updatePendingAlarmsCount = updatePendingAlarmsCount
    // 同时暴露当前计数的获取方法，方便其他组件访问
    window.getPendingAlarmsCount = () => pendingAlarmsCount.value
    
    // 从localStorage恢复折叠状态
    onMounted(() => {
      const savedCollapseState = localStorage.getItem('sidebarCollapse')
      if (savedCollapseState !== null) {
        isCollapse.value = JSON.parse(savedCollapseState)
      }
      
      // 开始轮询报警数量
      startPolling()
      
      // 监听页面可见性变化
      document.addEventListener('visibilitychange', handleVisibilityChange)
    })
    
    // 清理函数
    onBeforeUnmount(() => {
      // 停止监听器
      if (stopWatcher) {
        stopWatcher()
      }
      // 停止轮询
      stopPolling()
      // 清理页面可见性监听
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      // 清理全局方法
      if (window.updatePendingAlarmsCount) {
        delete window.updatePendingAlarmsCount
      }
    })
    
    return {
      menuItems,
      activeMenu,
      isCollapse,
      pendingAlarmsCount,
      handleMenuSelect,
      handleMenuClick,
      toggleCollapse
    }
  }
}
</script>

<style scoped>
.app-sidebar {
  position: relative;
  background: white;
  box-shadow: 2px 0 4px rgba(0,0,0,0.1);
  transition: width 0.3s;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.sidebar-menu {
  flex: 1;
  border-right: none;
  height: 100%;
}

.sidebar-menu:not(.el-menu--collapse) {
  width: 200px;
}

.sidebar-menu.el-menu--collapse {
  width: 64px;
}

.el-menu-item {
  height: 56px;
  line-height: 56px;
  margin: 0 8px;
  border-radius: 8px;
  transition: all 0.3s;
}

.el-menu-item:hover {
  background-color: #ecf5ff;
  color: #409EFF;
}

.el-menu-item.is-active {
  background-color: #409EFF;
  color: white;
}

.el-menu-item.is-active:hover {
  background-color: #337ecc;
}

.collapse-btn {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  width: 32px;
  height: 32px;
  background: #f5f7fa;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
  z-index: 100;
}

.collapse-btn:hover {
  background: #409EFF;
  color: white;
  border-color: #409EFF;
}

.el-icon {
  font-size: 16px;
}

/* 折叠状态下的样式调整 */
.sidebar-menu.el-menu--collapse .el-menu-item {
  padding: 0 20px;
}

.sidebar-menu.el-menu--collapse .collapse-btn {
  left: 32px;
}

/* 菜单标题包装器 */
.menu-title-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* 报警badge样式 */
.alarm-badge {
  position: absolute;
  top: -8px;
  right: -8px;
}

.alarm-badge-inline {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.alarm-badge-inline :deep(.el-badge__content) {
  background-color: #f56c6c;
  border: 2px solid #ffffff;
  animation: pulse-badge 2s ease-in-out infinite;
  position: static;
  transform: none;
  margin-left: 8px;
  min-width: 18px;
  height: 18px;
  line-height: 16px;
  font-size: 11px;
  border-radius: 9px;
}

@keyframes pulse-badge {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(245, 108, 108, 0.7);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 0 4px rgba(245, 108, 108, 0);
  }
}
</style>